import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Award, Home, UserCheck, Flag, MapPin, Target } from 'lucide-react';

const PermanentResidenceCitizenship = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Permanent Residence and Citizenship
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Plan your route to long-term status with clear timelines and solid evidence.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We assess eligibility for permanent residence and for citizenship or naturalisation where you qualify. We map the fastest credible route, plan document harvesting over time, and prepare a compliant application. We regularly handle the United Kingdom, United States, Canada, Germany and wider EU, Australia, and the UAE. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Temporary Status Holders</h3>
              <p className="text-gray-600 text-sm">Workers, students, or family members ready to move from temporary to permanent residence</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Eligible Residents</h3>
              <p className="text-gray-600 text-sm">Residents who meet day count or residence rules for citizenship or naturalisation</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Complex Cases</h3>
              <p className="text-gray-600 text-sm">Applicants with complex travel histories or past status gaps needing careful planning</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCheck className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Families</h3>
              <p className="text-gray-600 text-sm">Families wanting coordinated plans for principal and dependents</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Eligibility Audit</h3>
              <p className="text-gray-600 text-sm">Written assessment with most suitable route, fees, and realistic timeline</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Plan</h3>
              <p className="text-gray-600 text-sm">Harvesting plan covering residence evidence, taxes, language, and medical checks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Complete Application</h3>
              <p className="text-gray-600 text-sm">Quality-checked application ready for submission with appointments booked</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Flag className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Post-Approval Plan</h3>
              <p className="text-gray-600 text-sm">Passport and registration guidance after naturalisation where applicable</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Eligibility basics
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            Specific rules vary by country. The following themes are common.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Residence Requirements</h3>
                <p className="text-gray-600">Minimum lawful residence period and allowed absences</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Language & Knowledge</h3>
                <p className="text-gray-600">Language and knowledge requirements where applicable</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Good Character</h3>
                <p className="text-gray-600">Good character and disclosure of immigration or criminal history</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Tax & Integration</h3>
                <p className="text-gray-600">Tax compliance and evidence of integration or ties to the country</p>
              </div>
            </div>
            <div className="flex items-start md:col-span-2">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Route Continuity</h3>
                <p className="text-gray-600">Continued eligibility of the route that led to residence</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Typical Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Typical routes we cover at a high level
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United Kingdom</h3>
              <p className="text-gray-600 text-sm">Indefinite Leave to Remain based on work, family, or long residence, followed by naturalisation where criteria are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">United States</h3>
              <p className="text-gray-600 text-sm">Adjustment of Status or consular processing to permanent resident status, followed by naturalisation when eligible</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Canada</h3>
              <p className="text-gray-600 text-sm">Express Entry and provincial programs for permanent residence, followed by citizenship after residence and tax days are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Germany and wider EU</h3>
              <p className="text-gray-600 text-sm">Permanent residence or long-term EU residence for eligible residents, followed by citizenship where available</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Australia</h3>
              <p className="text-gray-600 text-sm">Permanent residence via ENS or points routes, followed by Australian citizenship when residence and character tests are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">UAE</h3>
              <p className="text-gray-600 text-sm">Long-term residence categories for eligible investors and professionals, and citizenship in limited scenarios</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            If your destination is not listed, ask during intake. We can coordinate additional countries through partners.
          </p>
        </div>
      </motion.section>

   
    </div>
  );
};

export default PermanentResidenceCitizenship;
